import { useEffect, useState, type ReactNode } from 'react'
import {
  fetchSpendCubeFace,
  formatSpend,
  formatSpendCompact,
  type SpendCubeDim,
  type SpendCubeFace as SpendCubeFaceData,
  type SpendCubeSummary,
  type SpendSummaryFilters,
} from './spendCubeApi'

const FACE_BLUE = '#5B9BD5'
const DIM_LABEL: Record<SpendCubeDim, string> = {
  parent: 'Supplier',
  rollup: 'Category',
  year: 'Year',
  bu: 'BU',
  plant: 'Plant',
  material: 'Material',
  department: 'Dept',
  status: 'Status',
  contractor: 'Contractor',
}

const FILTER_LABEL: Record<keyof SpendSummaryFilters, string> = {
  years: 'Year',
  costCenters: 'Cost center',
  bus: 'BU',
  statuses: 'Status',
  departments: 'Dept',
  rollups: 'Category',
  accountNames: 'Account',
  accountRollups: 'Account rollup',
  reportRollups: 'Report rollup',
  contractors: 'Contractor',
  parents: 'Supplier',
  sapItems: 'SAP item',
  materials: 'Material',
  plants: 'Plant',
}

function filterKey(dim: SpendCubeDim): keyof SpendSummaryFilters {
  switch (dim) {
    case 'parent':
      return 'parents'
    case 'rollup':
      return 'rollups'
    case 'year':
      return 'years'
    case 'bu':
      return 'bus'
    case 'plant':
      return 'plants'
    case 'material':
      return 'materials'
    case 'department':
      return 'departments'
    case 'status':
      return 'statuses'
    case 'contractor':
      return 'contractors'
  }
}

function displayKey(dim: SpendCubeDim, key: string): string {
  if (dim === 'rollup' && (key === '(blank)' || key === 'Unclassified')) return 'Unmapped'
  return key
}

function layerOptions(dim: SpendCubeDim, options: SpendCubeSummary['options'] | undefined): string[] {
  if (!options) return []
  if (dim === 'year') return options.years.map(String)
  const key = filterKey(dim)
  const values = options[key]
  return Array.isArray(values) ? values.map(String) : []
}

function selectedValues(dim: SpendCubeDim, filters: SpendSummaryFilters): string[] {
  const values = filters[filterKey(dim)]
  return values.map(String)
}

function cellColor(spend: number, max: number): { background: string; color: string } {
  if (max <= 0 || spend <= 0) return { background: '#F4F7FB', color: '#8AA0B8' }
  const t = Math.sqrt(Math.min(1, spend / max))
  const mix = (from: number, to: number) => Math.round(from + (to - from) * t)
  return {
    background: `rgb(${mix(244, 91)}, ${mix(247, 155)}, ${mix(251, 213)})`,
    color: t > 0.72 ? '#ffffff' : '#16324F',
  }
}

function IsoCube({
  rowDim,
  colDim,
  layerDim,
  onTurn,
}: {
  rowDim: SpendCubeDim
  colDim: SpendCubeDim
  layerDim: SpendCubeDim
  onTurn: (dim: SpendCubeDim) => void
}) {
  return (
    <svg viewBox="0 0 168 148" className="h-36 w-40 shrink-0" role="group" aria-label="Spend cube">
      <polygon points="84,14 150,48 84,82 18,48" fill="#E7F1FA" stroke="#9CC3E4" strokeWidth="1.2" />
      <polygon points="18,48 84,82 84,132 18,98" fill={FACE_BLUE} stroke="#3E7EB8" strokeWidth="1.2" />
      <polygon points="84,82 150,48 150,98 84,132" fill="#7EB0DE" stroke="#3E7EB8" strokeWidth="1.2" />
      <CubeFaceButton points="84,14 150,48 84,82 18,48" label={DIM_LABEL[layerDim]} x={84} y={50} fill="#16324F" dim={layerDim} onTurn={onTurn} />
      <CubeFaceButton points="18,48 84,82 84,132 18,98" label={DIM_LABEL[rowDim]} x={50} y={92} fill="#ffffff" dim={rowDim} onTurn={onTurn} />
      <CubeFaceButton points="84,82 150,48 150,98 84,132" label={DIM_LABEL[colDim]} x={118} y={92} fill="#ffffff" dim={colDim} onTurn={onTurn} />
    </svg>
  )
}

function CubeFaceButton({
  points,
  label,
  x,
  y,
  fill,
  dim,
  onTurn,
}: {
  points: string
  label: string
  x: number
  y: number
  fill: string
  dim: SpendCubeDim
  onTurn: (dim: SpendCubeDim) => void
}) {
  return (
    <g>
      <polygon points={points} fill="transparent" />
      <text x={x} y={y} textAnchor="middle" fill={fill} fontSize="11" fontWeight="700" pointerEvents="none">
        {label}
      </text>
      <polygon
        points={points}
        fill="transparent"
        className="cursor-pointer"
        role="button"
        aria-label={`Turn ${label} to the front`}
        tabIndex={0}
        onClick={() => onTurn(dim)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onTurn(dim)
          }
        }}
      />
    </g>
  )
}

export function SpendCubeFaceCard({
  companyId,
  filters,
  options,
  onFilters,
  onReset,
  filtersOpen,
  onToggleFilters,
  filtersPanel,
  onRowDim,
}: {
  companyId: string
  filters: SpendSummaryFilters
  options: SpendCubeSummary['options'] | undefined
  onFilters: (next: SpendSummaryFilters) => void
  onReset: () => void
  filtersOpen: boolean
  onToggleFilters: () => void
  filtersPanel: ReactNode
  onRowDim?: (dim: SpendCubeDim) => void
}) {
  const [rowDim, setRowDim] = useState<SpendCubeDim>('parent')
  const [colDim, setColDim] = useState<SpendCubeDim>('rollup')
  const [layerDim, setLayerDim] = useState<SpendCubeDim>('year')
  const [face, setFace] = useState<SpendCubeFaceData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    onRowDim?.(rowDim)
  }, [rowDim, onRowDim])

  useEffect(() => {
    let cancel = false
    setLoading(true)
    setError(null)
    fetchSpendCubeFace(companyId, rowDim, colDim, filters)
      .then((data) => {
        if (!cancel) setFace(data)
      })
      .catch((err: unknown) => {
        if (!cancel) setError(err instanceof Error ? err.message : 'Could not load the cube.')
      })
      .finally(() => {
        if (!cancel) setLoading(false)
      })
    return () => {
      cancel = true
    }
  }, [companyId, rowDim, colDim, filters])

  const withDim = (base: SpendSummaryFilters, dim: SpendCubeDim, value: string | null): SpendSummaryFilters => {
    const key = filterKey(dim)
    if (key === 'years') return { ...base, years: value ? [Number(value)] : [] }
    return { ...base, [key]: value ? [value] : [] }
  }

  const turn = (dim: SpendCubeDim) => {
    if (dim === layerDim) {
      onFilters(withDim(filters, dim, null))
      setLayerDim(colDim)
      setColDim(dim)
      return
    }
    if (dim === rowDim) {
      setRowDim(colDim)
      setColDim(dim)
      return
    }
    setColDim(rowDim)
    setRowDim(dim)
  }

  const setDimFilter = (dim: SpendCubeDim, value: string | null) => {
    onFilters(withDim(filters, dim, value))
  }

  const clearFilterKey = (key: keyof SpendSummaryFilters) => {
    onFilters({ ...filters, [key]: [] })
  }

  const chips = (Object.keys(FILTER_LABEL) as (keyof SpendSummaryFilters)[])
    .flatMap((key) => filters[key].map((value) => ({ key, value: String(value) })))

  const layers = layerOptions(layerDim, options)
  const showLayers = layers.length > 0 && layers.length <= 12
  const activeLayer = selectedValues(layerDim, filters)
  const maxCell = Math.max(0, ...(face?.cells.map((cell) => cell.spend) ?? [0]))
  const spendAt = (row: string, column: string) =>
    face?.cells.find((cell) => cell.row === row && cell.column === column)?.spend ?? 0

  return (
    <section className="rounded-[var(--radius-lg)] border border-es-soft bg-es-white shadow-[var(--shadow-card)] p-4 md:p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-es-muted">This slice</p>
          <p className="text-3xl font-bold tabular-nums text-es-navy mt-1">{formatSpend(face?.total ?? 0)}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {chips.map((chip) => (
              <button
                key={`${chip.key}-${chip.value}`}
                type="button"
                onClick={() => clearFilterKey(chip.key)}
                className="rounded-full border border-[#5B9BD5] bg-[#5B9BD5]/15 px-2.5 py-1 text-xs font-semibold text-es-navy"
              >
                {FILTER_LABEL[chip.key]}: {chip.key === 'rollups' ? displayKey('rollup', chip.value) : chip.value}
                <span className="ml-1 text-es-muted" aria-hidden>
                  ×
                </span>
                <span className="sr-only">Remove</span>
              </button>
            ))}
          </div>
        </div>
        <IsoCube rowDim={rowDim} colDim={colDim} layerDim={layerDim} onTurn={turn} />
      </div>

      {showLayers ? (
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label={`${DIM_LABEL[layerDim]} layer`}>
          <span className="text-xs font-semibold uppercase tracking-wider text-es-muted">{DIM_LABEL[layerDim]}</span>
          <LayerPill
            label="All"
            selected={activeLayer.length === 0}
            onClick={() => setDimFilter(layerDim, null)}
          />
          {layers.map((layer) => (
            <LayerPill
              key={layer}
              label={displayKey(layerDim, layer)}
              selected={activeLayer.length === 1 && activeLayer[0] === layer}
              onClick={() => setDimFilter(layerDim, layer)}
            />
          ))}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          aria-expanded={filtersOpen}
          onClick={onToggleFilters}
          className="rounded-[var(--radius-sm)] border border-es-soft px-3 py-1.5 text-xs font-semibold text-es-navy hover:border-[#5B9BD5]"
        >
          Filters{chips.length ? ` (${chips.length})` : ''}
        </button>
        <button type="button" onClick={() => { setRowDim('parent'); setColDim('rollup'); setLayerDim('year'); onReset() }} className="text-xs font-semibold text-es-signal hover:underline">
          Reset
        </button>
        {loading ? <span className="text-xs text-es-muted">Updating...</span> : null}
      </div>
      {filtersOpen ? <div className="flex flex-wrap gap-2 items-center">{filtersPanel}</div> : null}

      {error ? <p className="text-sm text-red-800">{error}</p> : null}

      <div key={`${rowDim}-${colDim}-${layerDim}`} className="overflow-x-auto [animation:cubeFade_220ms_ease]">
        <style>{`@keyframes cubeFade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }`}</style>
        {face && face.rows.length > 0 ? (
          <table className="border-separate border-spacing-1 text-left">
            <thead>
              <tr>
                <th className="sticky left-0 bg-es-white px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-es-muted">
                  {DIM_LABEL[rowDim]}
                </th>
                {face.columns.map((column) => (
                  <th key={column.key} className="px-1 py-1 text-[11px] font-semibold text-es-muted min-w-[4.5rem] max-w-[7rem]">
                    <span className="block truncate" title={displayKey(colDim, column.key)}>
                      {displayKey(colDim, column.key)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {face.rows.map((row) => (
                <tr key={row.key}>
                  <th className="sticky left-0 bg-es-white px-2 py-1 text-xs font-semibold text-es-navy text-left max-w-[9rem]">
                    <span className="block truncate" title={displayKey(rowDim, row.key)}>
                      {displayKey(rowDim, row.key)}
                    </span>
                  </th>
                  {face.columns.map((column) => {
                    const spend = spendAt(row.key, column.key)
                    const tone = cellColor(spend, maxCell)
                    const blocked = row.key === 'Other' || column.key === 'Other' || spend <= 0
                    return (
                      <td key={column.key} className="p-0">
                        <button
                          type="button"
                          disabled={blocked}
                          title={`${displayKey(rowDim, row.key)} · ${displayKey(colDim, column.key)} · ${formatSpend(spend)}`}
                          onClick={() => onFilters(withDim(withDim(filters, rowDim, row.key), colDim, column.key))}
                          className="h-10 w-full min-w-[4.5rem] rounded-sm px-1 text-[11px] font-semibold tabular-nums disabled:cursor-default"
                          style={{ backgroundColor: tone.background, color: tone.color }}
                        >
                          {spend > 0 ? formatSpendCompact(spend) : ''}
                        </button>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : loading ? null : (
          <p className="text-sm text-es-muted">No spend for this slice.</p>
        )}
      </div>
    </section>
  )
}

function LayerPill({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-xs font-semibold border ${
        selected ? 'border-[#5B9BD5] bg-[#5B9BD5] text-white' : 'border-es-soft bg-es-white text-es-navy hover:border-[#5B9BD5]'
      }`}
    >
      {label}
    </button>
  )
}
